import React, { useState } from 'react';
import styles from './ProfileCard.module.css';

interface ProfileCardProps {
  userProfile: {
    firstName: string;
    lastName: string;
    email: string;
    contactNumber: string;
    role: string;
    avatar?: string;
  };
  onSave?: (profile: any) => void;
}

const ProfileCard: React.FC<ProfileCardProps> = ({ userProfile, onSave }) => {
  const [formData, setFormData] = useState({
    firstName: userProfile.firstName,
    lastName: userProfile.lastName,
    email: userProfile.email,
    contactNumber: userProfile.contactNumber,
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isEditing, setIsEditing] = useState(false);

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.firstName.trim()) newErrors.firstName = 'Required';
    if (!formData.lastName.trim()) newErrors.lastName = 'Required';
    if (!formData.email.trim()) {
      newErrors.email = 'Required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Invalid email';
    }
    if (!formData.contactNumber.trim()) newErrors.contactNumber = 'Required';

    // Password validation (only if trying to change)
    if (formData.newPassword || formData.confirmPassword) {
      if (!formData.currentPassword) {
        newErrors.currentPassword = 'Required';
      }
      if (formData.newPassword && formData.newPassword.length < 8) {
        newErrors.newPassword = 'Min 8 chars';
      }
      if (formData.newPassword !== formData.confirmPassword) {
        newErrors.confirmPassword = 'Passwords do not match';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = () => {
    if (validateForm()) {
      onSave?.(formData);
      setIsEditing(false);
      setFormData({
        ...formData,
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
      });
    }
  };

  const handleCancel = () => {
    setFormData({
      firstName: userProfile.firstName,
      lastName: userProfile.lastName,
      email: userProfile.email,
      contactNumber: userProfile.contactNumber,
      currentPassword: '',
      newPassword: '',
      confirmPassword: '',
    });
    setErrors({});
    setIsEditing(false);
  };

  const getInitials = () => {
    return `${formData.firstName[0] || ''}${formData.lastName[0] || ''}`.toUpperCase();
  };

  return (
    <div className={styles.profileCard}>
      {/* Header */}
      <div className={styles.header}>
        <h3 className={styles.title}>My Profile</h3>
        <button
          className={styles.editButton}
          onClick={() => setIsEditing(!isEditing)}
          title={isEditing ? 'Cancel' : 'Edit'}
        >
          {isEditing ? (
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
              <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            </svg>
          ) : (
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
              <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" stroke="currentColor" strokeWidth="2"/>
              <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" stroke="currentColor" strokeWidth="2"/>
            </svg>
          )}
        </button>
      </div>

      {/* Avatar */}
      <div className={styles.avatarSection}>
        <div className={styles.avatar}>
          {userProfile.avatar ? (
            <img src={userProfile.avatar} alt="Profile" />
          ) : (
            <span>{getInitials()}</span>
          )}
        </div>
        <span className={styles.role}>{userProfile.role}</span>
      </div>

      {/* Form */}
      <div className={styles.form}>
        <div className={styles.formRow}>
          <div className={styles.formGroup}>
            <label>First Name</label>
            <input
              type="text"
              className={errors.firstName ? styles.error : ''}
              value={formData.firstName}
              onChange={(e) => handleInputChange('firstName', e.target.value)}
              disabled={!isEditing}
            />
            {errors.firstName && <span className={styles.errorText}>{errors.firstName}</span>}
          </div>

          <div className={styles.formGroup}>
            <label>Last Name</label>
            <input
              type="text"
              className={errors.lastName ? styles.error : ''}
              value={formData.lastName}
              onChange={(e) => handleInputChange('lastName', e.target.value)}
              disabled={!isEditing}
            />
            {errors.lastName && <span className={styles.errorText}>{errors.lastName}</span>}
          </div>
        </div>

        <div className={styles.formGroup}>
          <label>Email</label>
          <input
            type="email"
            className={errors.email ? styles.error : ''}
            value={formData.email}
            onChange={(e) => handleInputChange('email', e.target.value)}
            disabled={!isEditing}
          />
          {errors.email && <span className={styles.errorText}>{errors.email}</span>}
        </div>

        <div className={styles.formGroup}>
          <label>Contact</label>
          <input
            type="tel"
            className={errors.contactNumber ? styles.error : ''}
            value={formData.contactNumber}
            onChange={(e) => handleInputChange('contactNumber', e.target.value)}
            disabled={!isEditing}
          />
          {errors.contactNumber && <span className={styles.errorText}>{errors.contactNumber}</span>}
        </div>

        {/* Password Section */}
        {isEditing && (
          <>
            <div className={styles.divider}></div>
            
            <div className={styles.passwordSection}>
              <h4>Change Password</h4>
              
              <div className={styles.formGroup}>
                <label>Current Password</label>
                <input
                  type="password"
                  className={errors.currentPassword ? styles.error : ''}
                  value={formData.currentPassword}
                  onChange={(e) => handleInputChange('currentPassword', e.target.value)}
                  placeholder="Enter current password"
                />
                {errors.currentPassword && <span className={styles.errorText}>{errors.currentPassword}</span>}
              </div>

              <div className={styles.formGroup}>
                <label>New Password</label>
                <input
                  type="password"
                  className={errors.newPassword ? styles.error : ''}
                  value={formData.newPassword}
                  onChange={(e) => handleInputChange('newPassword', e.target.value)}
                  placeholder="Enter new password"
                />
                {errors.newPassword && <span className={styles.errorText}>{errors.newPassword}</span>}
              </div>

              <div className={styles.formGroup}>
                <label>Confirm Password</label>
                <input
                  type="password"
                  className={errors.confirmPassword ? styles.error : ''}
                  value={formData.confirmPassword}
                  onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
                  placeholder="Confirm new password"
                />
                {errors.confirmPassword && <span className={styles.errorText}>{errors.confirmPassword}</span>}
              </div>
            </div>
          </>
        )}
      </div>

      {/* Footer */}
      {isEditing && (
        <div className={styles.footer}>
          <button className={styles.cancelButton} onClick={handleCancel}>
            Cancel
          </button>
          <button className={styles.saveButton} onClick={handleSave}>
            Save
          </button>
        </div>
      )}
    </div>
  );
};

export default ProfileCard;